import * as React from 'react';
import LogPlusPersonsAutocomplete from './LogPlusPersonsAutocomplete';
import LogPlusDevicesAutocomplete from './LogPlusDevicesAutocomplete';
import LogPlusItSystemsAutocomplete from './LogPlusItSystemsAutocomplete';
import LogPlusAgreementsAutocomplete from './LogPlusAgreementsAutocomplete';
import LogPlusLicensesAutocomplete from './LogPlusLicensesAutocomplete';
import LogPlusServicesAutocomplete from './LogPlusServicesAutocomplete';
import { AutocompleteMode, IAgreementListItem, IData, IDeviceListItem, IItSystemListItem, ILicenseListItem, IPersonListItem, IServiceListItem, KindsList } from './IModels';
import { createStyles, makeStyles } from '@material-ui/styles';
import SearchButton from './SearchButton';

const useStyles = makeStyles((theme) =>
    createStyles({
        actions: {
            marginBottom: '7px',
        },
        actions__btn: {
            marginRight: '7px',
        },
    }),
);

interface IProps {
    multiple: boolean,
    
	disabled: boolean,
    kinds: (keyof IData)[],

    value: IData,
    onChange: (value: IData) => void,

    searchIn: string,
    error?: boolean,
    helperText?: string,

    notifier: { value: string, label: string },
    mode: AutocompleteMode,

    showGridTabs: string,
    filterId: string,
    relatedFilterId: string,
    manageFiltersPermission: boolean,

    gridColumns: any,
    relatedGridColumns: any,
    maxRows: number;
    loadOnFocus: boolean;
}

export default function LogPlusResourcesDataEditor(props: IProps) {
    const classes = useStyles();

    function isSelected(newResource: IDeviceListItem | IPersonListItem | IItSystemListItem | IAgreementListItem | ILicenseListItem | IServiceListItem, kind: KindsList): boolean {
        if (!props.value[kind])
            return false;

        for (let i = 0; i < props.value[kind].length; i++) {
            let resource = props.value[kind][i];

            if (resource.Id == newResource.Id) {
                return true;
            }
        }
        return false;
    }

    return (<>
        <div className={classes.actions}>
            <SearchButton
                disabled={props.disabled}
                className={classes.actions__btn}
                notifier={props.notifier}
                kinds={props.kinds}
                filterId={props.filterId}
                relatedFilterId={props.relatedFilterId}
                gridColumns={props.gridColumns}
                relatedGridColumns={props.relatedGridColumns}
                
                manageFiltersPermission={props.manageFiltersPermission}
                showGridTabs={props.showGridTabs}

                onSave={(newResources) => {
                    if (!newResources)
                        return;

                    let data = { ...props.value };
                    for (let i = 0; i < newResources.length; i++) {
                        let newResource = newResources[i];

                        let kind;
                        if (props.kinds.length == 1) {
                            kind = props.kinds[0];
                        }
                        else {
                            kind = (newResource as IDeviceListItem).HdKind;
                        }

                        if (!isSelected(newResource, kind)) {
                            if (!data[kind]) data[kind] = [];
                            data[kind].push(newResource);
                        }
                    }

                    props.onChange(data);
                }}
            />
        </div>
        <AutocompleEditor {...props} />
    </>)
}

function AutocompleEditor(props: IProps) {

    if (!props.value) {
        return null;
    }

    function getDevicesValues() {
        let devices: IDeviceListItem[] = [];
        for (let i = 0; i < props.kinds.length; i++) {
            let kind = props.kinds[i];
            let databyKind = props.value[kind] as IDeviceListItem[];
            if (databyKind)
                devices = devices.concat(databyKind);
        }

        return devices;
    }

    switch (props.kinds[0]) {
        case "Person":
            return (<LogPlusPersonsAutocomplete
                searchIn={props.searchIn?.split(',') as (keyof IPersonListItem)[]}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
                value={props.value.Person ?? []}
                filterId={props.filterId}
                onSelect={(r) => {
                    let data = { ...props.value };
                    data.Person = r;
                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />);
        case "ItSystem":
            return (<LogPlusItSystemsAutocomplete
                searchIn={props.searchIn?.split(',') as (keyof IItSystemListItem)[]}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
                value={props.value.ItSystem ?? []}
                filterId={props.filterId}
                onSelect={(r) => {
                    let data = { ...props.value };
                    data.ItSystem = r;
                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />);
        case "Service":
            return (<LogPlusServicesAutocomplete
                searchIn={props.searchIn}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
                value={props.value.Service ?? []}
                filterId={props.filterId}
                onSelect={(r) => {
                    let data = { ...props.value };
                    data.Service = r;
                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />);
        case "Agreement":
            return (<LogPlusAgreementsAutocomplete
                searchIn={props.searchIn}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
                value={props.value.Agreement ?? []}
                mode={props.mode}
                notifier={props.notifier}
                filterId={props.filterId}
                relatedFilterId={props.relatedFilterId}
                onSelect={(r) => {
                    let data = { ...props.value };
                    data.Agreement = r;
                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />)
        case "License":
            return (<LogPlusLicensesAutocomplete
                searchIn={props.searchIn}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
                value={props.value.License ?? []}
                mode={props.mode}
                notifier={props.notifier}
                filterId={props.filterId}
                relatedFilterId={props.relatedFilterId}
                onSelect={(r) => {
                    let data = { ...props.value };
                    data.License = r;
                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />)

        default:
            return (<LogPlusDevicesAutocomplete
                searchIn={props.searchIn}
                error={props.error}
                helperText={props.helperText}
                kinds={props.kinds}
                multiple={props.multiple}
                value={getDevicesValues()}
                mode={props.mode}
                notifier={props.notifier}
                filterId={props.filterId}
                relatedFilterId={props.relatedFilterId}
                onSelect={(r) => {
                    let data = { ...props.value };

                    for (let i = 0; i < props.kinds.length; i++) {
                        let kind = props.kinds[i];
                        data[kind] = r.filter(x => x.HdKind === kind) as any[];
                    }

                    props.onChange(data);
                }}
                loadOnFocus={props.loadOnFocus}
                maxRows={props.maxRows}
            />);
        
    }
}